import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  notification,
} from "antd";
import { Dayjs } from "dayjs";
import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "react-query";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IRankedSeasonAnnouncementService } from "@shared/interfaces/ranked-season-announcement-service.interface";
import {
  RankedSeasonAnnouncementDto,
  UpdateRankedSeasonAnnouncementDto,
} from "@shared/types/ranked-season-announcement/ranked-season-announcement.dto";
import { extractErrorMessage } from "@shared/utils/error-message";
import {
  PACIFIC_DATETIME_FORMAT,
  parseUtcIsoToPacificDayjs,
  pacificDayjsToUtcIso,
} from "@shared/utils/pacific-datetime";

type AnnouncementFormValues = {
  title?: string;
  body?: string;
  bannerDescription?: string;
  endsAt?: Dayjs | null;
  isActive: "1" | "0";
};

const emptyFormValues: AnnouncementFormValues = {
  title: undefined,
  body: undefined,
  bannerDescription: undefined,
  endsAt: null,
  isActive: "0",
};

const formValuesFromAnnouncement = (
  announcement: RankedSeasonAnnouncementDto | null | undefined,
): AnnouncementFormValues => {
  if (!announcement) {
    return emptyFormValues;
  }
  return {
    title: announcement.title ?? undefined,
    body: announcement.body ?? undefined,
    bannerDescription: announcement.bannerDescription ?? undefined,
    endsAt: announcement.endsAt
      ? parseUtcIsoToPacificDayjs(announcement.endsAt)
      : null,
    isActive: announcement.isActive ? "1" : "0",
  };
};

const applyAnnouncementToForm = (
  form: {
    resetFields: () => void;
    setFieldsValue: (values: AnnouncementFormValues) => void;
  },
  announcement: RankedSeasonAnnouncementDto | null | undefined,
  reset: boolean,
) => {
  const values = formValuesFromAnnouncement(announcement);
  if (reset) {
    form.resetFields();
  }
  form.setFieldsValue(values);
};

const RankedSeasonAnnouncementPage = () => {
  const [form] = Form.useForm<AnnouncementFormValues>();
  const hasHydratedFormRef = useRef(false);
  const syncFormAfterSaveRef = useRef(false);
  const announcementService = appInject<IRankedSeasonAnnouncementService>(
    DI_TOKENS.rankedSeasonAnnouncementService,
  );

  const getQuery = useQuery<RankedSeasonAnnouncementDto | null>(
    ["ranked-season-announcement"],
    () => announcementService.get(),
    { refetchOnMount: true },
  );

  const updateMutation = useMutation(
    (dto: UpdateRankedSeasonAnnouncementDto) => announcementService.update(dto),
    {
      onSuccess: (result) => {
        const description = result.jsonRemoved
          ? "S3 JSON removed (announcement inactive)"
          : result.uploadedFileUrl
            ? `Published: ${result.uploadedFileUrl}`
            : undefined;
        notification.success({
          message: "Announcement updated",
          description,
        });
        syncFormAfterSaveRef.current = true;
        void getQuery
          .refetch()
          .then((result) => {
            if (result.isSuccess) {
              applyAnnouncementToForm(form, result.data, true);
            }
          })
          .finally(() => {
            hasHydratedFormRef.current = true;
            syncFormAfterSaveRef.current = false;
          });
      },
      onError: (error: unknown) => {
        notification.error({
          message: "Failed to update announcement",
          description: extractErrorMessage(
            error,
            "Failed to update announcement",
          ),
        });
      },
    },
  );

  useEffect(() => {
    if (!getQuery.isSuccess) {
      return;
    }
    const shouldSync =
      !hasHydratedFormRef.current ||
      syncFormAfterSaveRef.current ||
      !form.isFieldsTouched();
    if (!shouldSync) {
      return;
    }

    applyAnnouncementToForm(
      form,
      getQuery.data,
      hasHydratedFormRef.current || syncFormAfterSaveRef.current,
    );
    hasHydratedFormRef.current = true;
    syncFormAfterSaveRef.current = false;
  }, [form, getQuery.data, getQuery.isSuccess]);

  const handleFinish = (values: AnnouncementFormValues) => {
    updateMutation.mutate({
      title: values.title?.trim() || null,
      body: values.body?.trim() || null,
      bannerDescription: values.bannerDescription?.trim() || null,
      endsAt: pacificDayjsToUtcIso(values.endsAt ?? null),
      isActive: values.isActive === "1",
    });
  };

  return (
    <Layout.Content style={{ padding: 24 }}>
      <Typography.Title level={3}>Season-end announcement</Typography.Title>
      <Typography.Paragraph type="secondary">
        Copy and countdown for the Ranked tab teaser. Tier prize amounts are
        edited on each tier. Saving republishes public S3 JSON
        (uploads/rankedseasonannouncement.json) for the app — no Nest client
        GET.
      </Typography.Paragraph>

      {getQuery.isLoading && (
        <Spin tip="Loading announcement...">
          <div style={{ minHeight: 120 }} />
        </Spin>
      )}

      {getQuery.isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to load announcement"
          description={extractErrorMessage(
            getQuery.error,
            "Failed to load announcement",
          )}
        />
      )}

      {getQuery.isSuccess && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ maxWidth: 720 }}
        >
          <Form.Item name="title" label="Title (countdown label)">
            <Input placeholder="e.g. SEASON 0 ENDS IN" />
          </Form.Item>

          <Form.Item name="body" label="Body">
            <Input.TextArea
              rows={4}
              placeholder="Main announcement paragraph shown above prizes"
            />
          </Form.Item>

          <Form.Item name="bannerDescription" label="Banner description">
            <Input.TextArea
              rows={3}
              placeholder="Short banner copy for the Ranked tab teaser"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="endsAt"
                label="Ends at (countdown, PST)"
                extra="Date and time are entered and shown in Pacific Time (PST/PDT)."
              >
                <DatePicker
                  showTime
                  style={{ width: "100%" }}
                  format={PACIFIC_DATETIME_FORMAT}
                  placeholder="Select date and time in PST"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Active"
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="1">Yes</Select.Option>
                  <Select.Option value="0">No</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateMutation.isLoading}
            >
              Save
            </Button>
          </Space>
        </Form>
      )}
    </Layout.Content>
  );
};

export default RankedSeasonAnnouncementPage;
